import * as React from 'react'
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router'
import { ArrowLeft, Loader2 } from 'lucide-react'
import { audienceLabel } from '../-components/learning-labels'
import type { LearningAudienceType } from '@/types/learning.types'
import { PageTitle } from '@/components/PageTitle'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useCreateLearningCourse } from '@/hooks/repositories/useLearningRepository'
import { requirePermission } from '@/lib/permissions/route-guards'
import { LEARNING_PERMISSION_KEYS } from '@/lib/permissions/learning-permissions'

export const Route = createFileRoute('/_app/learning/admin/new')({
  beforeLoad: requirePermission(LEARNING_PERMISSION_KEYS.author),
  component: RouteComponent,
})

/** Turns a title into the URL slug, so nobody has to think about slugs. */
function slugify(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

/**
 * Create a course (sc-1245).
 *
 * Deliberately asks for very little: a course is created as an empty draft and
 * everything else is edited afterwards. A long creation form would be a wall in
 * front of the work, and none of it can be published until lessons exist anyway.
 */
function RouteComponent() {
  const navigate = useNavigate()
  const createCourse = useCreateLearningCourse()

  const [title, setTitle] = React.useState('')
  const [slug, setSlug] = React.useState('')
  const [slugEdited, setSlugEdited] = React.useState(false)
  const [summary, setSummary] = React.useState('')
  const [audienceType, setAudienceType] =
    React.useState<LearningAudienceType>('ALL_STAFF')

  const effectiveSlug = slugEdited ? slug : slugify(title)
  const canSubmit = title.trim().length > 0 && effectiveSlug.length > 0

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault()
    if (!canSubmit) return

    // mutateAsync REJECTS on failure. Unguarded, the rejection escaped the
    // handler, navigation never ran, and the page simply sat there — the
    // failure mode reported from staging. The mutation's onError raises the
    // toast; this only has to stop the throw from eating the rest of the form.
    let created: { versions?: Array<{ id: string }> }
    try {
      created = (await createCourse.mutateAsync({
        title: title.trim(),
        slug: effectiveSlug,
        summary: summary.trim() || undefined,
        audienceType,
      })) as { versions?: Array<{ id: string }> }
    } catch {
      return
    }

    const versionId = created.versions?.[0]?.id
    if (versionId) {
      void navigate({
        to: '/learning/admin/$versionId',
        params: { versionId },
      })
    } else {
      void navigate({ to: '/learning/admin' })
    }
  }

  return (
    <div className="py-4">
      <PageTitle title="New course" />

      <Button asChild variant="ghost" size="sm" className="mb-3 -ml-2">
        <Link to="/learning/admin">
          <ArrowLeft className="mr-1 size-4" aria-hidden />
          Courses
        </Link>
      </Button>

      <div className="mb-4">
        <h1 className="text-xl font-bold tracking-tight sm:text-2xl">
          New course
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Creates an empty draft. Add lessons next — a course cannot be
          published without at least one.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="max-w-2xl">
        <Card>
          <CardContent className="space-y-5 py-5">
            <div className="space-y-2">
              <Label htmlFor="course-title">Title</Label>
              <Input
                id="course-title"
                value={title}
                onChange={(event) => setTitle(event.target.value)}
                placeholder="Code of Ethics"
                autoFocus
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="course-slug">URL slug</Label>
              <Input
                id="course-slug"
                value={effectiveSlug}
                onChange={(event) => {
                  setSlugEdited(true)
                  setSlug(slugify(event.target.value))
                }}
                placeholder="code-of-ethics"
                className="font-mono text-sm"
              />
              <p className="text-xs text-muted-foreground">
                Follows the title unless you change it. Permanent once set — it
                is how the course is linked to.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="course-summary">Summary</Label>
              <Textarea
                id="course-summary"
                value={summary}
                onChange={(event) => setSummary(event.target.value)}
                placeholder="One or two lines on what this covers."
                rows={3}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="course-audience">Audience</Label>
              <Select
                value={audienceType}
                onValueChange={(value) =>
                  setAudienceType(value as LearningAudienceType)
                }
              >
                <SelectTrigger id="course-audience" className="w-full sm:w-64">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {/* PERMISSION omitted — see the editor's audience card. */}
                  {(['ALL_STAFF', 'DEPARTMENT', 'INDIVIDUAL'] as const).map(
                    (type) => (
                      <SelectItem key={type} value={type}>
                        {audienceLabel(type)}
                      </SelectItem>
                    ),
                  )}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Who this is assigned to when it is published. Pick the exact
                levels — or specific people — after creating it.
              </p>
            </div>
          </CardContent>
        </Card>

        <div className="mt-4 flex items-center gap-2">
          <Button type="submit" disabled={!canSubmit || createCourse.isPending}>
            {createCourse.isPending && (
              <Loader2 className="mr-1 size-4 animate-spin" aria-hidden />
            )}
            Create draft
          </Button>
          <Button asChild type="button" variant="ghost">
            <Link to="/learning/admin">Cancel</Link>
          </Button>
        </div>
      </form>
    </div>
  )
}
